import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useInlineNotifications } from '../../../../hooks'
import { NotificationInline } from '../../../Notification'
import Text from '../../../Text'
import { WidgetItemPipelineBase } from '../../../Widget'
import { useDocFlowMappingContext } from '../../DocFlow.hooks'
import { isDocFlowActiveWidgetResult } from '../../DocFlow.utils.widgets'
import { DocFlowMappingProcessCell } from '../../DocFlowMapping'
import { isDocFlowUnvisitedWidget } from '../DocFlowWidgets.utils'

import { DocFlowMappingWidgetResultNotificationType } from './DocFlowMappingWidgetResult.constants'
import {
  getWidgetResultItems,
  getWidgetResultNotifications,
  isWidgetError,
} from './DocFlowMappingWidgetResult.utils'

import * as styles from './DocFlowMappingWidgetResult.module.css'

function DocFlowMappingWidgetResult(props) {
  const { name } = props

  const {
    activeWidget,
    handleClickPreviousWidget,
    isPdfFinishedRendering,
    processData,
    setPdfViewerSelectAreaInitialParams,
  } = useDocFlowMappingContext()

  const { t } = useTranslation()

  const {
    handleAddNotification,
    handlePrependNotification,
    handleRemoveNotification,
    notifications,
  } = useInlineNotifications()

  const items = getWidgetResultItems({
    documents: processData?.[DocFlowMappingProcessCell.ResultSplittedDocuments],
    users: processData?.[DocFlowMappingProcessCell.ResultMappedUsers],
  })

  const unvisited = isDocFlowUnvisitedWidget(
    processData,
    DocFlowMappingProcessCell.ResultPhase
  )

  const isResultActive = isDocFlowActiveWidgetResult(activeWidget)

  useEffect(() => {
    if (isResultActive && isPdfFinishedRendering) {
      setPdfViewerSelectAreaInitialParams()
    }
  }, [isPdfFinishedRendering, isResultActive])

  const hasError = isWidgetError({ isResultActive, processData })

  const widgetDone = isResultActive && !hasError

  const hasDuplicates =
    processData?.[DocFlowMappingProcessCell.HasUploadingResultsDuplicates]

  const widgetNotifications = getWidgetResultNotifications(t)

  useEffect(() => {
    if (isResultActive) {
      if (hasError) {
        handleAddNotification(
          widgetNotifications[DocFlowMappingWidgetResultNotificationType.Error]
        )
      } else {
        handleRemoveNotification(
          widgetNotifications[DocFlowMappingWidgetResultNotificationType.Error]
            .id
        )

        handlePrependNotification(
          widgetNotifications[
            DocFlowMappingWidgetResultNotificationType.Success
          ]
        )
      }
    }
  }, [hasError, isResultActive, name])

  useEffect(() => {
    if (unvisited) {
      Object.values(widgetNotifications).forEach(notification => {
        handleRemoveNotification(notification.id)
      })
    }
  }, [unvisited])

  useEffect(() => {
    if (hasDuplicates) {
      handleAddNotification(
        widgetNotifications[
          DocFlowMappingWidgetResultNotificationType.HasDuplicates
        ]
      )
    } else {
      handleRemoveNotification(
        widgetNotifications[
          DocFlowMappingWidgetResultNotificationType.HasDuplicates
        ].id
      )
    }
  }, [hasDuplicates])

  return (
    <WidgetItemPipelineBase
      {...props}
      done={widgetDone}
      hasError={hasError}
      name={name}
      unvisited={unvisited}
      onClickPrevious={handleClickPreviousWidget}
    >
      {notifications.map(item => (
        <NotificationInline {...item} key={item.text} />
      ))}

      {items.map(item => (
        <div className={styles.items} key={item.title}>
          <Text isSecondary>{item.title}</Text>
          <Text>{item.text}</Text>
        </div>
      ))}
    </WidgetItemPipelineBase>
  )
}

export default DocFlowMappingWidgetResult
